import { ROLE_GROUPS, PERMISSIONS, getRolePermissions } from "./permissions.js";

import { ROLES } from "../routes";
/**
 * Centralized role/permission helpers.
 * - Permissions-first: user.permissions is canonical (array of strings)
 * - permission_checks is a convenience map { perm: boolean }
 */
export const isSuperAdmin = (user) => {
  if (!user) return false;
  // backend flag first, then common role names
  if (user.is_superuser) return true;
};

export const isAdmin = (user) => {
  if (!user) return false;
  if (isSuperAdmin(user)) return true;
  const role = (user.role || "").toString().toLowerCase();
  return role === ROLES["ADMIN"];
};

export const hasPermission = (user, perm) => {
  if (!user) return false;
  if (isAdmin(user)) return true;

  if (user.permissions && Array.isArray(user.permissions)) {
    return user.permissions.includes(perm);
  }
  if (user.permissions?.permission_checks?.[perm] === true) {
    return true;
  }
};

export const hasAnyPermission = (user, permissions) => {
  if (!user || !Array.isArray(permissions)) return false;
  if (isAdmin(user)) return true;
  return permissions.some((perm) => hasPermission(user, perm));
};

export const hasAllPermissions = (user, permissions) => {
  if (!user || !Array.isArray(permissions)) return false;
  if (isAdmin(user)) return true;
  return permissions.every((perm) => hasPermission(user, perm));
};

export const hasRole = (user, role) => {
  if (!user) return false;
  const userRole = (user.role || "").toString().toLowerCase();
  const requiredRole = (role || "").toString().toLowerCase();
  return userRole === requiredRole;
};

export const hasAnyRole = (user, roles) => {
  if (!user || Array.isArray(roles)) return false;
  return roles.some((role) => hasRole(user, role));
};

export const belongsToGroup = (user, group) => {
  if (!user) return false;
  if (isAdmin(user)) return true;
  if (user.groups && Array.isArray(user.groups)) {
    return user.groups.includes(group);
  }
  const userRole = user.role;
  if (!userRole) return false;
  for (const [groupName, groupRoles] of Object.entries(ROLE_GROUPS)) {
    if (groupName === group && groupRoles.includes(userRole)) {
      return true;
    }
  }
  return false;
};

export const belongsToAnyGroup = (user, groups) => {
  if (!user || !Array.isArray(groups)) return false;
  if (isAdmin(user)) return true;
  return groups.some((group) => belongsToGroup(user, group));
};

export const getUserPermissions = (user) => {
  if (!user) return [];
  if (isAdmin(user)) return Object.values(PERMISSIONS);
  if (user.permissions && Array.isArray(user.permissions)) {
    return user.permissions;
  }
  if (user.role) {
    return getRolePermissions(user.role);
  }
  return [];
};

export const canAccessResource = (
  user,
  requiredPermissions = [],
  requiredGroups = [],
  requireAll = false,
) => {
  if (!user) return false;
  if (isAdmin(user)) return true;
  let hasRequiredPermissions = true;
  let hasRequiredGroups = true;

  if (requiredPermissions.length > 0) {
    hasRequiredPermissions = requireAll
      ? hasAllPermissions(user, requiredPermissions)
      : hasAnyPermission(user, requiredPermissions);
  }
  if (requiredGroups.length > 0) {
    hasRequiredGroups = requireAll
      ? requiredGroups.every((group) => belongsToGroup(user, group))
      : belongsToAnyGroup(user, requiredGroups);
  }
  return hasRequiredPermissions && hasRequiredGroups;
};

/**
 * Check if user can edit or archive a project
 * @param {Object} project - Project data
 * @param {Object} user - Current user
 * @returns {boolean} - True if user can edit or archive
 */
export const canEditOrArchive = (project, user) => {
  if (!user) return false;

  // Admins can edit/archive any project
  if (user.role === ROLES["ADMIN"]) return true;

  // Project managers can only edit/archive projects they manage
  if (user.role === ROLES["PROJECT_MANAGER"]) {
    const isManager = project?.manager?.id === user.id;
    return isManager;
  }

  return false;
};

/**
 * Check if user can edit or add items to a project
 * @param {Object} user - Current user
 * @param {Object} projectData - Project data
 * @returns {boolean} - True if user can edit or add
 */
export const canAdd = (user, projectData) => {
  if (!user || !projectData) return false;

  // Admins can edit/add to any project
  if (user.role === ROLES["ADMIN"]) return true;

  // Project managers can edit/add to projects they manage
  if (user.role === ROLES["PROJECT_MANAGER"]) {
    const isManager = projectData?.manager?.id === user.id;
    const isMember = projectData?.members?.some(
      (member) => member.id === user.id,
    );
    return isManager || isMember;
  }

  // Finance Officers can add to projects they're assigned to
  if (user.role === ROLES["FINANCE_OFFICER"]) {
    const isMember = projectData?.members?.some(
      (member) => member.id === user.id,
    );
    return isMember;
  }

  // Staff are view-only — cannot create items
  return false;
};

export const canEdit = (user, entity, project) => {
  if (!user || !entity) return false;
  if (user.role === ROLES["ADMIN"]) return true;
  if (
    user.role === ROLES["PROJECT_MANAGER"] &&
    (entity?.manager?.id === user.id || // for Project
      entity?.project?.manager?.id === user.id || // for sub-entities
      entity?.owner?.id === user.id || // for owned entities
      project.manager?.id === user.id) //PM who managers the project
  )
    return true;
  if (
    (user.role === ROLES["STAFF"] || user.role === ROLES["FINANCE_OFFICER"]) &&
    entity?.owner?.id === user.id
  )
    return true;
  return false;
};

export const canEditOuts = (user, entity, project) => {
  if (!user || !entity) return false;
  if (user.role === ROLES["ADMIN"]) return true;
  if (
    user.role === ROLES["PROJECT_MANAGER"] &&
    (entity?.project?.manager?.id === user.id || // for sub-entities
      entity?.owner?.id === user.id || // for owned entities
      project.manager?.id === user.id) //PM who managers the project
  )
    return true;
  if (
    (user.role === ROLES["STAFF"] || user.role === ROLES["FINANCE_OFFICER"]) &&
    entity?.owner?.id === user.id
  )
    return true;
  
};
export const canEditExpense = (user, expense, project) => {
  const isOwner = expense?.owner?.id === user.id;
  if (!user || !expense) return false;
  if (user.role === ROLES["ADMIN"]) return true;
  if (
    user.role === ROLES["PROJECT_MANAGER"] &&
    (expense?.project?.manager?.id === user.id || // for sub-entities
      expense?.owner?.id === user.id || // for owned entities
      project.manager?.id === user.id) //PM who managers the project
  )
    return true;
  // FINANCE_OFFICER and member
  if (
    user.role === ROLES["FINANCE_OFFICER"] &&
    project.members?.some((m) => m.id === user.id)
  )
    return true;
  if (user.role === ROLES["STAFF"] && expense?.owner?.id === user.id)
    return true;
  return false;
};

export const canDeleteExpense = (user, expense, project) => {
  
  if (!user || !expense) return false;
  if (user.role === ROLES["ADMIN"]) return true;
  if (
    user.role === ROLES["PROJECT_MANAGER"] &&
    (expense?.project?.manager?.id === user.id || // for sub-entities
      expense?.owner?.id === user.id || // for owned entities
      project.manager?.id === user.id) //PM who managers the project
  )
    return true;
  // FINANCE_OFFICER only if member can delete
  if (
    user.role === ROLES["FINANCE_OFFICER"] &&
    project.members?.some((m) => m.id === user.id)
  )
    return true;
  return false;
};

export const canDelete = (user, document, project) => {
  if (!user || !document) return false;
  if (user.role === ROLES["ADMIN"]) return true;
  if (user.id === document.uploaded_by) return true;
  if (user.role === ROLES["PROJECT_MANAGER"] && project.manager?.id === user.id)
    return true;
  return false;
};

export const canEditAfterCompleted = (user, project) => {
  if (user.role === ROLES["ADMIN"]) return true;
  if (
    user.role === ROLES["PROJECT_MANAGER"] &&
    project?.manager?.id === user.id
  )
    return true;
};

export const canArchive = (user) => {
  return user.role === ROLES["ADMIN"];
};
// Helper: Check if user is a project member
const isProjectMember = (user, project) => {
  return project.members?.some((m) => m.id === user.id);
};

// Helper: Check if user is project manager
const isProjectManager = (user, project) => {
  return project.manager?.id === user.id;
};

// Helper: Check create permissions
const getCreatePermissions = (user, project) => {
  const isAdmin = user.role === ROLES["ADMIN"];
  const isPM = user.role === ROLES["PROJECT_MANAGER"];
  const isStaff = user.role === ROLES["STAFF"];
  const isFO = user.role === ROLES["FINANCE_OFFICER"];
  const isMember = isProjectMember(user, project);
  const isProjManager = isProjectManager(user, project);

  if (isAdmin) return { canView: true, canEdit: true };
  if (isPM && (isProjManager || isMember)) return { canView: true, canEdit: true };
  if (isStaff && isMember) return { canView: true, canEdit: false };
  if (isFO && isMember) return { canView: true, canEdit: true };
  
  return { canView: false, canEdit: false };
};

// Helper: Check permissions for completed items
const getCompletedItemPermissions = (user, project) => {
  const isAdmin = user.role === ROLES["ADMIN"];
  const isPM = user.role === ROLES["PROJECT_MANAGER"] && isProjectManager(user, project);
  const isStaff = user.role === ROLES["STAFF"];
  const isFO = user.role === ROLES["FINANCE_OFFICER"];

  if (isAdmin || isPM) return { canView: true, canEdit: true };
  if (isStaff || isFO) return { canView: true, canEdit: false };
  
  return { canView: false, canEdit: false };
};

// Helper: Check permissions for active items
const getActiveItemPermissions = (user, project, item) => {
  const isAdmin = user.role === ROLES["ADMIN"];
  const isProjManager = isProjectManager(user, project);
  const isOwner = item?.owner?.id === user.id;
  const isFO = user.role === ROLES["FINANCE_OFFICER"];

  if (isAdmin) return { canView: true, canEdit: true };
  if (user.role === ROLES["PROJECT_MANAGER"] && isProjManager) {
    return { canView: true, canEdit: true };
  }
  // PM who owns the item but is not the project manager
  if (user.role === ROLES["PROJECT_MANAGER"] && isOwner) return { canView: true, canEdit: true };
  if (isFO) return { canView: true, canEdit: false };
  if (user.role === ROLES["STAFF"]) return { canView: true, canEdit: false };
  
  return { canView: false, canEdit: false };
};

export const canEditOutcomeOutput = (user, project, item, isCreate) => {
  if (isCreate) {
    return getCreatePermissions(user, project);
  }

  const isCompleted = item?.status === "completed";
  
  if (isCompleted) {
    return getCompletedItemPermissions(user, project);
  }

  return getActiveItemPermissions(user, project, item);
};

/**
 * Check if user can view an outcome
 * @param {Object} user - Current user
 * @param {Object} project - Project containing the outcome
 * @param {Object} outcome - The outcome to check (optional)
 * @returns {boolean} - True if user can view
 */
export const canViewOutcome = (user, project, outcome = null) => {
  if (!user || !project) return false;

  // Admins can view any outcome
  if (user.role === ROLES["ADMIN"]) return true;

  // Project managers can view outcomes in projects they manage
  if (
    user.role === ROLES["PROJECT_MANAGER"] &&
    user.id === project?.manager?.id
  ) {
    return true;
  }

  // Staff can only view outcomes in projects they're assigned to
  if (user.role === ROLES["STAFF"]) {
    // Check if user is a member of the project
    const isMember = project?.members?.some((member) => member.id === user.id);
    return isMember;
  }

  return false;
};

/**
 * Check if user can edit an outcome
 * @param {Object} user - Current user
 * @param {Object} project - Project containing the outcome
 * @param {Object} outcome - The outcome to check
 * @returns {boolean} - True if user can edit
 */
export const canEditOutcome = (user, outcome, project) => {
  if (!outcome || !user) return false;

  // Admins can edit any outcome
  if (user.role === ROLES["ADMIN"]) return true;

  // Project managers can edit outcomes in projects they manage or own
  if (
    user.role === ROLES["PROJECT_MANAGER"] &&
    (user.id === project?.manager?.id || user.id === outcome?.owner?.id)
  ) {
    return true;
  }

  // Staff are view-only for outcomes

  return false;
};
/**
 * Helper function to check if user can delete project entities
 * (outcomes, activities, etc.)
 * @param {Object} user - Current user
 * @param {Object} project - Project containing the entity
 * @returns {boolean} - True if user can delete
 */
const canDeleteProjectEntity = (user, project) => {
  if (!user || !project) return false;
  if (user.role === ROLES["ADMIN"]) return true;
  return (
    user.role === ROLES["PROJECT_MANAGER"] &&
    user.id === project?.manager?.id
  );
};

/**
 * Check if user can delete an outcome
 * @param {Object} user - Current user
 * @param {Object} project - Project containing the outcome
 * @param {Object} outcome - The outcome to check (optional)
 * @returns {boolean} - True if user can delete
 */
export const canDeleteOutcome = (user, project, outcome = null) => {
  return canDeleteProjectEntity(user, project);
};

/**
 * Check if user can delete an activity
 * @param {Object} user - Current user
 * @param {Object} project - Project containing the activity
 * @param {Object} activity - The activity to check (optional)
 * @returns {boolean} - True if user can delete
 */
export const canDeleteActivity = (user, project, activity = null) => {
  return canDeleteProjectEntity(user, project);
};

/**
 * Check if user can edit an output
 * @param {Object} user - Current user
 * @param {Object} project - Project containing the output
 * @param {Object} output - The output to check
 * @returns {boolean} - True if user can edit
 */
export const canEditOutput = (user, project, output) => {
  if (!output || !user) return false;

  // Admins can edit any output
  if (user.role === ROLES["ADMIN"]) return true;

  // Project managers can only edit outputs in projects they manage
  if (
    user.role === ROLES["PROJECT_MANAGER"] &&
    user.id === project?.manager?.id
  ) {
    return true;
  }

  // Staff are view-only for outputs

  return false;
};

/**
 * Check if user can edit an indicator
 * Admin and project manager of the project can edit any indicator.
 * Staff can edit indicators they are responsible for.
 * @param {Object} user - Current user
 * @param {Object} indicator - The indicator to check
 * @param {Object} project - Project containing the indicator
 * @returns {boolean}
 */
export const canEditIndicator = (user, indicator, project) => {
  if (!user || !project) return false;
  if (user.role === ROLES["ADMIN"]) return true;
  if (
    user.role === ROLES["PROJECT_MANAGER"] &&
    user.id === project?.manager?.id
  ) {
    return true;
  }
  return user.role === ROLES["STAFF"] && indicator?.responsible === user.id;
};

/**
 * Check if user can delete an indicator
 * Only admins and project managers of the project can delete indicators.
 * @param {Object} user - Current user
 * @param {Object} project - Project containing the indicator
 * @returns {boolean}
 */
export const canDeleteIndicator = (user, project) => {
  if (!user || !project) return false;
  if (user.role === ROLES["ADMIN"]) return true;
  return (
    user.role === ROLES["PROJECT_MANAGER"] &&
    user.id === project?.manager?.id
  );
};

export default {
  hasAnyPermission,
  hasAllPermissions,
  hasRole,
  hasAnyRole,
  belongsToGroup,
  belongsToAnyGroup,
  getUserPermissions,
  canAccessResource,
  canEditOrArchive,
  canEditOutcomeOutput,
  canViewOutcome,
  canEditOutcome,
  canDeleteOutcome,
  canDeleteActivity,
  canEditOutput,
  canEditIndicator,
  canDeleteIndicator,
  canEdit,
  canAdd,
};
